import { X, Send, Copy, Share2, MessageCircle } from 'lucide-react';
import { usePreferences } from '@/store/preferences';

interface ShareSheetProps {
  open: boolean;
  onClose: () => void;
  onCopyLink: () => void;
  onShareExternal: () => void;
  onSendMessage?: () => void;
  onSendInApp?: () => void;
}

const ShareSheet = ({ open, onClose, onCopyLink, onShareExternal, onSendMessage, onSendInApp }: ShareSheetProps) => {
  const { t } = usePreferences();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60" onClick={onClose}>
      <div
        className="mx-auto w-full max-w-lg rounded-t-2xl bg-card pb-safe animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center py-3">
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
        </div>

        <div className="flex items-center justify-between border-b border-border px-4 pb-3">
          <h3 className="text-base font-semibold text-foreground">{t.share}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-1">
          {onSendInApp && (
            <button
              onClick={() => { onSendInApp(); onClose(); }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors press-scale hover:bg-secondary"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
                <Send className="h-4.5 w-4.5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{t.send_in_nextchamp}</p>
                <p className="text-xs text-muted-foreground">{t.send_in_nextchamp_desc}</p>
              </div>
            </button>
          )}

          <button
            onClick={() => { onCopyLink(); onClose(); }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors press-scale hover:bg-secondary"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
              <Copy className="h-4.5 w-4.5 text-foreground" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{t.copy_link}</p>
              <p className="text-xs text-muted-foreground">{t.copy_link_desc}</p>
            </div>
          </button>

          <button
            onClick={() => { onShareExternal(); onClose(); }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors press-scale hover:bg-secondary"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
              <Share2 className="h-4.5 w-4.5 text-foreground" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{t.share_externally}</p>
              <p className="text-xs text-muted-foreground">{t.share_externally_desc}</p>
            </div>
          </button>

          {onSendMessage && (
            <button
              onClick={() => { onSendMessage(); onClose(); }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors press-scale hover:bg-secondary"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
                <MessageCircle className="h-4.5 w-4.5 text-foreground" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{t.send_via_message}</p>
                <p className="text-xs text-muted-foreground">{t.send_via_message_desc}</p>
              </div>
            </button>
          )}
        </div>

        <div className="px-4 pb-4">
          <button onClick={onClose} className="w-full rounded-full bg-secondary py-3 text-sm font-semibold text-foreground press-scale">
            {t.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareSheet;
